import React, { useState, useEffect, useRef } from 'react';
import { secureGet } from '../config/api';
import { useTranslation } from 'react-i18next';
import { formatAmount } from '../utils/format';
import { useHistory } from 'react-router-dom';
import ProductPopup from '../modules/Products/ProductPopup';
import SnackbarNotification from './SnackbarNotification';

import chevronIcon from '../icons/chevron-down.svg';
import noImage from '../icons/noImageIcon.svg';

const GlobalSearch = () => {
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState('product');
  const [showEntityDropdown, setShowEntityDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { t } = useTranslation();
  const history = useHistory();
  
  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const entityDropdownRef = useRef(null); 

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        entityDropdownRef.current &&
        !entityDropdownRef.current.contains(event.target)
      ) {
        setShowEntityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleImageError = (e) => {
    e.target.src = noImage;
    e.target.style.width = "50px";
    e.target.style.height = "50px";
  };

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;
    if (!selectedEntity) return;

    const fetchData = async () => {
      try {
        if (selectedEntity === 'product') {
          const allProducts = await secureGet("/products/unpaginated", showSnackbar, { signal });
          setProducts(allProducts.data);
          setFilteredProducts(allProducts.data);
        } else if (selectedEntity === 'client') {
          const allClients = await secureGet("/clients", showSnackbar, { signal });
          setClients(allClients.data);
          setFilteredClients(allClients.data);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(`Error fetching ${selectedEntity}s:`, error);
        }
      }
    };

    fetchData();
    return () => abortController.abort();
  }, [selectedEntity]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (selectedEntity === 'product') {
      const filtered = products.filter((product) =>
        product.name.toLowerCase().includes(value.toLowerCase()) ||
        product.reference.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else if (selectedEntity === 'client') {
      const filtered = clients.filter((client) =>
        client.name.toLowerCase().includes(value.toLowerCase()) ||
        client.code.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredClients(filtered);
    }
  };

  const handleClientClick = (row) => {
    history.push({
      pathname: `/clients/details/${row.id}`,
      state: { client: row },
    });
  };

  const handleEntitySelect = (entity) => {
    setSelectedEntity(entity);
    setShowEntityDropdown(false);
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  return (
    <div style={{
      flexGrow: 1,
      flexShrink: 1,
      minWidth: '250px',
      maxWidth: '100%',
    }} className={`relative h-12 bg-opacity-50 rounded-lg flex items-center px-4 ${showDropdown ? "bg-white" : "bg-[#FFFFFF7C] "} z-10`}>

      <div className="relative flex items-center" ref={entityDropdownRef}>
        <div
          onClick={() => setShowEntityDropdown(!showEntityDropdown)}
          className="mr-2 h-full w-[110px] font-semibold px-4 bg-transparent border-none outline-none text-black hover:bg-gray-100 cursor-pointer flex items-center">
          <span>{selectedEntity === 'product' ? t('rows.products') : t('rows.clients')}</span>
          <img src={chevronIcon} alt="chevron"
            className={`w-4 h-4 ml-2 transform transition-transform ${showEntityDropdown ? 'rotate-180' : ''}`} />
        </div>

        <div className="h-6 w-[2px] bg-gray-300 mx-2 "></div>

        {showEntityDropdown && (
          <div className="absolute top-[40px] left-[-16px] w-full bg-white rounded-lg shadow-lg max-h-[200px] overflow-y-auto z-50">
            <div className="flex flex-col items-center">
              <div
                className="font-medium py-[8px] text-left pl-8  hover:bg-gray-100 cursor-pointer w-full "
                onClick={() => handleEntitySelect('product')}
              >
                {t('rows.products')}
              </div>
              <div className="w-[80%] h-[1px] mx-auto" style={{ backgroundColor: '#A8ADC3', opacity: 0.22 }}></div>
              <div
                className="font-medium py-[8px] text-left pl-8 hover:bg-gray-100 cursor-pointer w-full "
                onClick={() => handleEntitySelect('client')}
              >
                {t('rows.clients')}
              </div>
            </div>
          </div>
        )}
      </div>

      <input
        type="text"
        placeholder={selectedEntity === 'product' ? t('placeholder.global_searchP') : t('placeholder.global_searchC')}
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        className="w-full h-full pl-4 border-none outline-none bg-transparent font-semibold text-gray-500 text-base"
      />

      {showDropdown && (
        <div className="absolute top-14 left-[0px] w-full bg-white rounded-[12px] shadow-lg max-h-[400px] overflow-y-auto z-50">
          {selectedEntity === 'product' && filteredProducts.length > 0 && (
            filteredProducts.map((product, index) => (
              <React.Fragment key={product.id}>
                <div className="flex items-center py-3 px-8 hover:bg-gray-100 cursor-pointer" onClick={() => handleProductClick(product.id)}>
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-12 h-12 object-contain mr-4"
                    onError={handleImageError}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-[12px] text-[#ACAFC6] font-bold">{product.reference}</p>
                      <span
                        className="text-[10px] text-white font-bold px-1 rounded-[12px]"
                        style={{ backgroundColor: product.family?.color }}
                      >
                        {product.familyName}
                      </span>
                    </div>
                    <p className="text-[16px] text-[#38485C] font-semibold">{product.name}</p>
                  </div>
                  <p className="text-[16px] font-bold text-[#6B78F8]">{formatAmount(product.priceTTC)}</p>
                </div>
                {index < filteredProducts.length - 1 && (
                  <div className="w-[90%] h-[1px] mx-6" style={{ backgroundColor: '#A8ADC3', opacity: 0.22 }}></div>
                )}
              </React.Fragment>
            ))
          )}

          {selectedEntity === 'client' && filteredClients.length > 0 && (
            filteredClients.map((client, index) => (
              <div key={client.id} className="flex items-center py-3 px-8 hover:bg-gray-100 cursor-pointer" onClick={() => handleClientClick(client)}>
                <div className="flex">
                  <p className="text-[16px] text-[#6B78F8] font-bold">{client.code}</p>
                  <span className="mx-2"> - </span>
                  <p className="text-[16px] text-[#38485C] font-semibold">{client.name}</p>
                </div>
              </div>
            ))
          )}

          {filteredProducts.length === 0 && filteredClients.length === 0 && (
            <div className="p-4 text-center text-gray-500">{t('placeholder.no_data')}</div>
          )}
        </div>
      )}

      {selectedProduct && (
        <ProductPopup
          productId={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={snackbar.manual ? null : 6000}
      />
    </div>
  );
};

export default GlobalSearch;
